<template>
  <div>
    <PageHeader :title="surveyTitle ? `Responses: ${surveyTitle}` : 'Survey Responses'" showBack>
      <template #actions>
        <b-button size="sm" variant="outline-primary" @click="exportCsv" :disabled="isExporting || !responses.length" class="mr-2">
          <i class="material-icons mr-1" style="font-size: 14px; vertical-align: middle;">download</i>
          {{ isExporting ? 'Exporting...' : 'Export CSV' }}
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="fetchResponses" :disabled="isLoading">
          <i class="material-icons mr-1" style="font-size: 14px; vertical-align: middle;">refresh</i>
          Refresh
        </b-button>
      </template>
    </PageHeader>

    <!-- Summary cards -->
    <b-row class="mb-3">
      <b-col sm="4">
        <b-card no-body class="summary-card">
          <b-card-body class="d-flex align-items-center">
            <div class="summary-icon">
              <i class="material-icons">people</i>
            </div>
            <div class="ml-3">
              <div class="summary-value">{{ responses.length }}</div>
              <div class="summary-label">Total Responses</div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col sm="4">
        <b-card no-body class="summary-card">
          <b-card-body class="d-flex align-items-center">
            <div class="summary-icon">
              <i class="material-icons">quiz</i>
            </div>
            <div class="ml-3">
              <div class="summary-value">{{ questionCount }}</div>
              <div class="summary-label">Questions</div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col sm="4">
        <b-card no-body class="summary-card">
          <b-card-body class="d-flex align-items-center">
            <div class="summary-icon">
              <i class="material-icons">schedule</i>
            </div>
            <div class="ml-3">
              <div class="summary-value">{{ latestResponse }}</div>
              <div class="summary-label">Latest Response</div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Analytics Section -->
    <b-card v-if="responses.length && questionAnalytics.length" no-body class="mb-3 analytics-card">
      <b-card-header
        class="d-flex align-items-center justify-content-between cursor-pointer analytics-header"
        @click="analyticsExpanded = !analyticsExpanded"
      >
        <div class="d-flex align-items-center">
          <i class="material-icons mr-2" style="font-size: 20px;">bar_chart</i>
          <span class="font-weight-bold">Analytics</span>
          <span class="text-muted small ml-2">({{ questionAnalytics.length }} questions)</span>
        </div>
        <i class="material-icons expand-icon" :class="{ rotated: analyticsExpanded }">expand_more</i>
      </b-card-header>
      <b-card-body v-if="analyticsExpanded" class="pt-2">
        <div
          v-for="(q, qIdx) in questionAnalytics"
          :key="qIdx"
          class="analytics-question"
          :class="{ 'border-bottom': qIdx < questionAnalytics.length - 1 }"
        >
          <div class="d-flex align-items-center justify-content-between mb-2">
            <span class="analytics-question-title">{{ q.title }}</span>
            <span class="answer-type-badge" :class="`type-${q.type}`">{{ typeLabel(q.type) }}</span>
          </div>

          <!-- Boolean questions: Yes/No bar -->
          <div v-if="q.type === 'boolean'" class="analytics-bars">
            <div class="analytics-bar-row">
              <span class="analytics-bar-label">Yes</span>
              <div class="analytics-bar-track">
                <div
                  class="analytics-bar-fill bar-yes"
                  :style="{ width: q.data.yesPercent + '%' }"
                ></div>
              </div>
              <span class="analytics-bar-count">{{ q.data.yes }} ({{ q.data.yesPercent }}%)</span>
            </div>
            <div class="analytics-bar-row">
              <span class="analytics-bar-label">No</span>
              <div class="analytics-bar-track">
                <div
                  class="analytics-bar-fill bar-no"
                  :style="{ width: q.data.noPercent + '%' }"
                ></div>
              </div>
              <span class="analytics-bar-count">{{ q.data.no }} ({{ q.data.noPercent }}%)</span>
            </div>
          </div>

          <!-- Single select / Multi select: option counts -->
          <div v-else-if="q.type === 'single_select' || q.type === 'multi_select'" class="analytics-bars">
            <div
              v-for="(opt, oIdx) in q.data.options"
              :key="oIdx"
              class="analytics-bar-row"
            >
              <span class="analytics-bar-label">{{ opt.label }}</span>
              <div class="analytics-bar-track">
                <div
                  class="analytics-bar-fill"
                  :class="barColorClass(oIdx)"
                  :style="{ width: opt.percent + '%' }"
                ></div>
              </div>
              <span class="analytics-bar-count">{{ opt.count }} ({{ opt.percent }}%)</span>
            </div>
          </div>

          <!-- Text questions -->
          <div v-else-if="q.type === 'short_text' || q.type === 'long_text'" class="analytics-text-summary">
            <span class="text-muted">{{ q.data.count }} responses</span>
          </div>

          <!-- File questions -->
          <div v-else-if="q.type === 'file_type'" class="analytics-text-summary">
            <span class="text-muted">{{ q.data.count }} files uploaded</span>
          </div>

          <!-- Fallback -->
          <div v-else class="analytics-text-summary">
            <span class="text-muted">{{ q.data.count }} responses</span>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Loading state -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner />
    </div>

    <!-- Empty state -->
    <b-card v-else-if="!responses.length" class="text-center py-5">
      <i class="material-icons mb-3" style="font-size: 48px; opacity: 0.25;">inbox</i>
      <h5 class="text-muted">No responses yet</h5>
      <p class="text-muted small mb-0">Responses will appear here once people start completing the survey.</p>
    </b-card>

    <!-- Response list -->
    <div v-else>
      <b-card v-for="(response, index) in responses" :key="response.id" no-body class="mb-2 response-card" :class="{ 'border-primary': expandedId === response.id }">
        <!-- Response header row (clickable) -->
        <b-card-body class="cursor-pointer response-row" @click="toggleDetail(response.id)">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="respondent-avatar" :style="{ backgroundColor: avatarColor(index) }">
                {{ initials(response.contact_name) }}
              </div>
              <div class="ml-3">
                <router-link
                  v-if="response.contact"
                  :to="{ name: 'contact-detail', params: { id: response.contact } }"
                  class="font-weight-bold respondent-link"
                  @click.native.stop
                >
                  {{ response.contact_name }}
                </router-link>
                <span v-else class="font-weight-bold">{{ response.contact_name }}</span>
                <div class="text-muted small">
                  <span v-if="response.respondent_email">{{ response.respondent_email }}</span>
                  <span v-if="response.respondent_email && response.respondent_phone"> &middot; </span>
                  <span v-if="response.respondent_phone">{{ response.respondent_phone }}</span>
                </div>
              </div>
            </div>
            <div class="d-flex align-items-center">
              <span class="text-muted small response-date mr-2">{{ formatDate(response.submitted_at) }}</span>
              <i class="material-icons text-muted expand-icon" :class="{ rotated: expandedId === response.id }">expand_more</i>
            </div>
          </div>
        </b-card-body>

        <!-- Expanded answer detail -->
        <div v-if="expandedId === response.id" class="border-top">
          <b-card-body class="pt-3 pb-2">
            <div v-for="(answer, aIdx) in response.answers" :key="answer.id" class="answer-row" :class="{ 'border-bottom': aIdx < response.answers.length - 1 }">
              <div class="d-flex align-items-start justify-content-between mb-1">
                <span class="answer-question">{{ answer.question_title }}</span>
                <span class="answer-type-badge" :class="`type-${answer.question_type}`">{{ typeLabel(answer.question_type) }}</span>
              </div>
              <div class="answer-value" :class="{ 'text-muted': !hasAnswer(answer) }">
                {{ formatAnswer(answer) }}
              </div>
            </div>
          </b-card-body>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import axios from '../../http';
import { formatShortDateTime, formatTimeAgo } from '../../common/utils';
import PageHeader from '../../components/Ui/PageHeader.vue';

const AVATAR_COLORS = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#6f42c1', '#fd7e14', '#20c997'];
const BAR_COLOR_CLASSES = ['bar-color-0', 'bar-color-1', 'bar-color-2', 'bar-color-3', 'bar-color-4', 'bar-color-5', 'bar-color-6', 'bar-color-7'];

export default {
  name: 'SurveyResponses',
  components: { PageHeader },
  data() {
    return {
      surveyTitle: '',
      responses: [],
      questionCount: 0,
      isLoading: false,
      isExporting: false,
      expandedId: null,
      analyticsExpanded: true,
    };
  },
  computed: {
    latestResponse() {
      if (!this.responses.length) return '\u2014';
      const latest = this.responses.reduce((a, b) =>
        new Date(a.submitted_at) > new Date(b.submitted_at) ? a : b
      );
      return this.formatRelative(latest.submitted_at);
    },
    questionAnalytics() {
      if (!this.responses.length) return [];

      // Build a map of question_title -> { type, answers[] }
      const questionMap = {};
      const questionOrder = [];

      this.responses.forEach(response => {
        if (!response.answers) return;
        response.answers.forEach(answer => {
          const key = answer.question_title;
          if (!questionMap[key]) {
            questionMap[key] = {
              title: answer.question_title,
              type: answer.question_type,
              answers: [],
            };
            questionOrder.push(key);
          }
          questionMap[key].answers.push(answer);
        });
      });

      return questionOrder.map(key => {
        const q = questionMap[key];
        const total = q.answers.length;
        let data = {};

        if (q.type === 'boolean') {
          let yes = 0;
          let no = 0;
          q.answers.forEach(a => {
            const v = a.value;
            let boolVal = null;
            if (typeof v === 'boolean') {
              boolVal = v;
            } else if (v && typeof v === 'object' && v.value !== undefined) {
              boolVal = v.value;
            } else if (typeof v === 'string') {
              boolVal = v.toLowerCase() === 'true' || v.toLowerCase() === 'yes';
            }
            if (boolVal === true) yes++;
            else if (boolVal === false) no++;
          });
          const answered = yes + no;
          data = {
            yes,
            no,
            yesPercent: answered ? Math.round((yes / answered) * 100) : 0,
            noPercent: answered ? Math.round((no / answered) * 100) : 0,
          };
        } else if (q.type === 'single_select') {
          const counts = {};
          q.answers.forEach(a => {
            const v = a.value;
            let selected = null;
            if (typeof v === 'string' && v) {
              selected = v;
            } else if (v && typeof v === 'object') {
              selected = v.value || v.text || null;
            }
            if (selected) {
              counts[selected] = (counts[selected] || 0) + 1;
            }
          });
          const answered = Object.values(counts).reduce((s, c) => s + c, 0);
          const options = Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([label, count]) => ({
              label,
              count,
              percent: answered ? Math.round((count / answered) * 100) : 0,
            }));
          data = { options, total: answered };
        } else if (q.type === 'multi_select') {
          const counts = {};
          let totalSelections = 0;
          q.answers.forEach(a => {
            const v = a.value;
            let values = [];
            if (v && Array.isArray(v.values)) {
              values = v.values;
            } else if (Array.isArray(v)) {
              values = v;
            } else if (typeof v === 'string' && v) {
              values = v.split(',').map(s => s.trim()).filter(Boolean);
            }
            values.forEach(val => {
              counts[val] = (counts[val] || 0) + 1;
              totalSelections++;
            });
          });
          const options = Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .map(([label, count]) => ({
              label,
              count,
              percent: totalSelections ? Math.round((count / totalSelections) * 100) : 0,
            }));
          data = { options, total: totalSelections };
        } else if (q.type === 'short_text' || q.type === 'long_text') {
          const answered = q.answers.filter(a => {
            const v = a.value;
            if (!v) return false;
            if (typeof v === 'string') return !!v.trim();
            if (v.text) return !!v.text.trim();
            return false;
          }).length;
          data = { count: answered };
        } else if (q.type === 'file_type') {
          const uploaded = q.answers.filter(a => {
            const v = a.value;
            if (!v) return false;
            if (typeof v === 'string') return !!v;
            if (v.fileName || v.file || v.url) return true;
            return Object.keys(v).length > 0;
          }).length;
          data = { count: uploaded };
        } else {
          data = { count: total };
        }

        return {
          title: q.title,
          type: q.type,
          data,
        };
      });
    },
  },
  methods: {
    ...mapActions('survey', ['getSurvey', 'getSurveyResponses']),
    formatDate: formatShortDateTime,
    formatRelative: formatTimeAgo,
    toggleDetail(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    initials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    },
    avatarColor(index) {
      return AVATAR_COLORS[index % AVATAR_COLORS.length];
    },
    barColorClass(index) {
      return BAR_COLOR_CLASSES[index % BAR_COLOR_CLASSES.length];
    },
    typeLabel(type) {
      const map = {
        short_text: 'Short Text',
        long_text: 'Long Text',
        boolean: 'Yes / No',
        single_select: 'Choice',
        multi_select: 'Multi',
        file_type: 'File',
      };
      return map[type] || type;
    },
    hasAnswer(answer) {
      const v = answer.value;
      if (!v) return false;
      if (typeof v === 'string') return !!v;
      if (v.text) return !!v.text;
      if (v.value !== undefined) return v.value !== '' && v.value !== null;
      if (v.values && Array.isArray(v.values)) return v.values.length > 0;
      return Object.keys(v).length > 0;
    },
    formatAnswer(answer) {
      const v = answer.value;
      if (!v) return 'No answer';
      if (typeof v === 'string') return v || 'No answer';
      if (v.text) return v.text;
      if (v.value !== undefined) {
        if (typeof v.value === 'boolean') return v.value ? 'Yes' : 'No';
        return String(v.value) || 'No answer';
      }
      if (v.values && Array.isArray(v.values)) return v.values.length ? v.values.join(', ') : 'No answer';
      if (v.fileName) return v.fileName;
      if (Object.keys(v).length === 0) return 'No answer';
      return JSON.stringify(v);
    },
    async exportCsv() {
      this.isExporting = true;
      try {
        const surveyId = this.$route.params.id;
        const response = await axios.get(`api/surveys/${surveyId}/export-responses/`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const filename = `survey_${surveyId}_responses.csv`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.makeToast('success', 'CSV exported successfully');
      } catch (err) {
        this.makeToast('danger', 'Failed to export CSV');
      } finally {
        this.isExporting = false;
      }
    },
    async fetchResponses() {
      this.isLoading = true;
      try {
        const res = await this.getSurveyResponses(this.$route.params.id);
        this.responses = res.data || [];
      } catch (err) { this.makeToast('danger', 'Failed to load responses'); }
      finally { this.isLoading = false; }
    },
  },
  async created() {
    try {
      const surveyRes = await this.getSurvey(this.$route.params.id);
      this.surveyTitle = surveyRes.data.title;
      this.questionCount = (surveyRes.data.questions || []).length;
    } catch (err) { /* ignore */ }
    this.fetchResponses();
  },
};
</script>

<style scoped>
/* Summary cards */
.summary-card {
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.summary-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f4f4f5;
  color: #181818;
}
.summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #181818;
  line-height: 1.2;
}
.summary-label {
  font-size: 0.75rem;
  color: #858796;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.03em;
}

/* Analytics section */
.analytics-card {
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.analytics-header {
  background-color: #fafafa;
  border-bottom: 1px solid #eee;
  padding: 0.75rem 1.25rem;
}
.analytics-question {
  padding: 1rem 0;
}
.analytics-question-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #181818;
}
.analytics-bars {
  margin-top: 0.25rem;
}
.analytics-bar-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.4rem;
}
.analytics-bar-label {
  width: 100px;
  font-size: 0.8rem;
  color: #555;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.analytics-bar-track {
  flex: 1;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 0.5rem;
}
.analytics-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
  min-width: 0;
}
.analytics-bar-count {
  font-size: 0.75rem;
  color: #666;
  flex-shrink: 0;
  min-width: 60px;
  text-align: right;
}
.analytics-text-summary {
  padding: 0.25rem 0;
  font-size: 0.85rem;
}

/* Bar colors */
.bar-yes { background-color: #1cc88a; }
.bar-no { background-color: #e74a3b; }
.bar-color-0 { background-color: #4e73df; }
.bar-color-1 { background-color: #1cc88a; }
.bar-color-2 { background-color: #36b9cc; }
.bar-color-3 { background-color: #f6c23e; }
.bar-color-4 { background-color: #e74a3b; }
.bar-color-5 { background-color: #6f42c1; }
.bar-color-6 { background-color: #fd7e14; }
.bar-color-7 { background-color: #20c997; }

/* Response cards */
.response-card {
  transition: border-color 0.2s, box-shadow 0.2s;
}
.response-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.response-row { padding: 0.75rem 1rem; }
.response-date { white-space: nowrap; }
.cursor-pointer {
  cursor: pointer;
}
.respondent-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}
.respondent-link {
  color: var(--primary-color, #4e73df);
  text-decoration: none;
}
.respondent-link:hover {
  text-decoration: underline;
}
.expand-icon {
  transition: transform 0.2s ease;
  font-size: 20px;
}
.expand-icon.rotated {
  transform: rotate(180deg);
}

/* Answers */
.answer-row {
  padding: 0.65rem 0;
}
.answer-question {
  font-weight: 600;
  font-size: 0.875rem;
  color: #181818;
}
.answer-value {
  font-size: 0.9rem;
  color: #333;
  margin-top: 0.15rem;
}
.answer-type-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  flex-shrink: 0;
}
.type-short_text { background: #e8f4fd; color: #1976d2; }
.type-long_text { background: #e8f5e9; color: #388e3c; }
.type-boolean { background: #fff3e0; color: #f57c00; }
.type-single_select { background: #f3e5f5; color: #7b1fa2; }
.type-multi_select { background: #fce4ec; color: #c62828; }
.type-file_type { background: #e0f2f1; color: #00796b; }
</style>
